import React, { FC, useState, useEffect } from 'react'
import { useAlert } from 'react-alert'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { fetchMemberList } from '../../../../states/containers/member/listSlice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { fetchTeamList } from '../../../../states/containers/team/listSlice'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { fetchHolidaySchedule } from '../../../../states/containers/holidaySchedule/holidayScheduleSlice'
import { Link, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { LangValue } from '../../../../states/types/common'
import { URL } from '../../../constants/url'
import { RootState } from '../../../../states/containers/store'

interface LabelGroupCategory {
  id: number | null
  category_name: string
  color: string
  parent_id: number | null
  label_group_count: number
}

interface ApprovalGroup {
  id: number
  name: string
  approvers: {
    member_id: number | null
    member_name: string | null
    member_kana: string | null
    member_profile_image: string | null,
    team_name: string,
    priority: number,
  }[]
  members: {
    member_id: number
    member_name: string
    member_kana: string | null
    member_profile_image: string | null,
  }[]
}

interface LabelGroup {
  label_group_id: number
  label_group_name: string
  label_group_code: string | null
  label_group_color: string
  disabled: boolean
  labels: {
    label_id: number
    label_name: string
    label_code: string | null
    label_color: string
    description: string | null
    disabled: boolean
  }[]
}

const TAB_LIST = [
  {
    type: "display_project",
    label: "表示対象プロジェクト",
  },
  {
    type: "display_category",
    label: "表示対象カテゴリー",
  },
  {
    type: "project_and_label",
    label: "業務仕分け対象プロジェクト・業務",
  },
  {
    type: "approval_group",
    label: "申請承認グループ",
  },
  {
    type: "mail_setting",
    label: "配信メール・アラート",
  },
  {
    type: "other_setting",
    label: "その他の設定",
  },
]

const OTHER_SETTING_LIST = [
  {
    key: "measure_restriction_setting",
    title: "計測停止の制限設定",
    description: "ユーザーによる計測停止の制限を設定できます。",
  },
  {
    key: "web_browser_setting",
    title: "Webブラウザの詳細非表示設定",
    description: "URLとページタイトルを非表示にできます。",
  },
  {
    key: "threshold_time_setting",
    title: "業務仕分けボードに表示する作業データの最小時間",
    description: "業務仕分けボードに表示する作業データの最小時間を設定します。",
  },
  {
    key: "create_task_by_url_setting",
    title: "業務仕分けボードの作業データをURLごとに作成する",
    description: "業務仕分けボードの作業データをURLごとに作成するようにします。",
  },
  {
    key: "exclude_generate_member_setting",
    title: "作業データの非自動生成設定",
    description: "作業データの非自動生成を設定します。",
  },
  {
    key: "learning_scope_setting",
    title: "仕分け提案に使用する過去データの範囲設定",
    description: "作業データの仕分け提案に使用する過去データの範囲を設定します。",
  },
  {
    key: "create_online_meeting_task_setting",
    title: "オンラインミーティング時の作業データ設定",
    description: "オンラインミーティング時、操作等がない時でも作業データを自動作成するように設定します。",
  },
]

const TMP_APPROVAL_GROUP: ApprovalGroup[] = [
  {
    id: 1,
    name: "承認グループ1",
    approvers: [
      {
        member_id: 1,
        member_name: "マネージャー1",
        member_kana: "まねーじゃー1",
        member_profile_image: null,
        team_name: "なんとか部",
        priority: 1,
      },
      {
        member_id: 2,
        member_name: "マネージャー2",
        member_kana: "まねーじゃー2",
        member_profile_image: null,
        team_name: "なんとか課",
        priority: 2,
      },
      {
        member_id: 3,
        member_name: "マネージャー3",
        member_kana: "まねーじゃー3",
        member_profile_image: null,
        team_name: "なんとか係",
        priority: 3,
      },
    ],
    members: [
      {
        member_id: 4,
        member_name: "ユーザー1",
        member_kana: "めんばー1",
        member_profile_image: null,
      },
      {
        member_id: 5,
        member_name: "ユーザー2",
        member_kana: "めんばー2",
        member_profile_image: null,
      },
    ],
  },
  {
    id: 2,
    name: "承認グループ2",
    approvers: [
      {
        member_id: 6,
        member_name: "マネージャー6",
        member_kana: "まねーじゃー6",
        member_profile_image: null,
        team_name: "なんとか部",
        priority: 1,
      },
      {
        member_id: null,
        member_name: null,
        member_kana: null,
        member_profile_image: null,
        team_name: "なんとか課",
        priority: 2,
      },
      {
        member_id: 7,
        member_name: "マネージャー7",
        member_kana: "まねーじゃー7",
        member_profile_image: null,
        team_name: "なんとか係",
        priority: 3,
      },
    ],
    members: [
      {
        member_id: 8,
        member_name: "ユーザー8",
        member_kana: "めんばー8",
        member_profile_image: null,
      },
      {
        member_id: 9,
        member_name: "ユーザー9",
        member_kana: "めんばー9",
        member_profile_image: null,
      },
    ],
  },
]

const TMP_LABEL_GROUP: LabelGroup[] = Array(20).fill(null).map((_v, i) => (
  {
    label_group_id: i + 1,
    label_group_name: `プロジェクト${i + 1}`,
    label_group_code: null,
    label_group_color: "#999999",
    disabled: false,
    labels: Array(20).fill(null).map((_w, j) => (
      {
        label_id: j + 1,
        label_name: `業務${j + 1}`,
        label_code: null,
        label_color: "#CCCCCC",
        description: null,
        disabled: false,
      }
    )),
  }
))

const TMP_CATEGORY: LabelGroupCategory[] = [
  {
    id: 1,
    category_name: "カテゴリーA",
    color: "#FF0000",
    parent_id: null,
    label_group_count: 20,
  },
  {
    id: 2,
    category_name: "カテゴリーAの子",
    color: "#f6ff00",
    parent_id: 1,
    label_group_count: 10,
  },
  {
    id: 3,
    category_name: "カテゴリーAの孫",
    color: "#00eeff",
    parent_id: 2,
    label_group_count: 5,
  },
  {
    id: 4,
    category_name: "カテゴリーAの子2",
    color: "#0011ff",
    parent_id: 1,
    label_group_count: 8,
  },
  {
    id: 4,
    category_name: "カテゴリーB",
    color: "#ff00f7",
    parent_id: null,
    label_group_count: 30,
  },
]

const TMP_ALERT = Array(3).fill(null).map((_v, i) => (
  {
    id: i + 1,
    name: `アラート${i + 1}`,
    target: "チーム名",
    count: 10,
    cycle: "翌日",
  }
))

const Detail: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [selectTab, setSelectTab] = useState<string>("display_project")
  const [openLabelGroupIds, setOpenLabelGroupIds] = useState<number[]>([])

  const [labelGroupCategories, setLabelGroupCategories] = useState<LabelGroupCategory[]>(TMP_CATEGORY)

  const buildCategoryList = (level = 0, parent_id?: number | null, rules?: boolean[]):JSX.Element => {
    let list = []
    const levelCount = level + 1
    if (parent_id === 0) return (<></>)
    if (parent_id) {
      list = labelGroupCategories.filter((v) => v.parent_id === parent_id)
    } else {
      list = labelGroupCategories.filter((v) => v.parent_id === null)
    }
    if (!list.length) return (<></>)
    return (
      <>
        {
          list.map((v, i) => {
            const ruleList = [...rules?.map((rule, index) => (rules.length === index + 1 ? !rule : rule)) || [], list.length === i + 1]
            return (
              <React.Fragment key={`project_category_row_${v.category_name}`}>
                <div className="project_category_row">
                  <div className="project_category_header">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      {
                        ruleList.map((w, j) => {
                          if (!j) return (<React.Fragment key={`${v.category_name}_${j + 0}_${level}`} />)
                          return (
                            <div
                              key={`${v.category_name}_${j + 0}_${level}`}
                              style={{
                                width: "14px",
                                fontSize: "16px",
                              }}
                            >
                              {
                                ruleList.length === j + 1 ? (
                                  <>
                                    {w ? (<>┗</>) : (<>┣</>)}
                                  </>
                                ) : (
                                  <>
                                    {w && (<>┃</>)}
                                  </>
                                )
                              }
                            </div>
                          )
                        })
                      }
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: "40px",
                          border: "solid 1px #999999",
                          padding: "8px 12px",
                          width: "fit-content",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={['fas', 'folder']}
                            className="icon"
                            style={{
                              color: v.color,
                              fontSize: "18px",
                            }}
                          />
                          {v.category_name}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "baseline",
                            fontSize: "10px",
                          }}
                        >
                          プロジェクト全
                          <span
                            style={{
                              fontSize: "16px",
                            }}
                          >
                            {v.label_group_count.toLocaleString()}
                          </span>
                          件
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {buildCategoryList(levelCount, v.id, ruleList)}
              </React.Fragment>
            )
          })
        }
      </>
    )
  }

  const changeOpenLabelGroup = (labelGroupId: number) => {
    if (openLabelGroupIds.includes(labelGroupId)) {
      setOpenLabelGroupIds(openLabelGroupIds.filter((v) => v !== labelGroupId))
    } else {
      setOpenLabelGroupIds([...openLabelGroupIds, labelGroupId])
    }
  }

  useEffect(() => {
    dispatch(
      fetchMemberList(() => {
        setIsLoading(false)
      }),
    )
    dispatch(
      fetchTeamList(() => { /* do nothing */ }),
    )
    dispatch(fetchHolidaySchedule())
  }, [])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - ユーザー設定 - 詳細`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_MEMBER.path, language)}
        >
          ユーザー設定
        </Link>
        &nbsp;&gt;&nbsp;
        詳細
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          ユーザー詳細
        </h1>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              ユーザー情報を取得しています…
            </>
          </LoadingSquare>
        ) : (
          <>
            <section
              className="page_contents content-area"
              style={{
                position: "sticky",
                top: "-178px",
                zIndex: "1",
              }}
            >
              <div
                className="content-block"
                style={{
                  margin: 0,
                  padding: 0,
                }}
              >
                <div
                  className="block-detail"
                  style={{
                    display: "flex",
                    flexFlow: "column",
                    gap: "40px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: "24px",
                    }}
                  >
                    <div
                      style={{
                        width: "120px",
                        position: "relative",
                        display: "flex",
                        flexFlow: "column",
                        gap: "16px",
                      }}
                    >
                      <div
                        style={{
                          width: "120px",
                          height: "120px",
                          backgroundColor: "#CCCCCC",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FontAwesomeIcon
                          icon={['fas', 'user']}
                          style={{
                            color: "#FFFFFF",
                            fontSize: "80px",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          position: "absolute",
                          top: "110px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          fontSize: "9px",
                          padding: "2px 8px",
                          border: "1px solid #4CAF50",
                          borderRadius: "20px",
                          backgroundColor: "#d8ffda",
                          color: "#4caf50",
                          whiteSpace: "nowrap",
                        }}
                      >
                        アクティブ
                      </div>
                      <button
                        type="button"
                        style={{
                          border: "solid 1px #CCCCCC",
                          fontSize: "8px",
                          textAlign: "center",
                          padding: "4px",
                        }}
                      >
                        プロフィール画像の設定
                      </button>
                    </div>
                    <div
                      style={{
                        width: "300px",
                        display: "flex",
                        flexFlow: "column",
                        gap: "8px",
                      }}
                    >
                      <div
                        style={{
                          position: "relative",
                        }}
                      >
                        <input
                          type="text"
                          defaultValue="名前 名前"
                          placeholder="名前を入力"
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            padding: "8px 12px",
                            fontSize: "18px",
                            backgroundColor: "#FFFFFF",
                            width: "100%",
                            color: "#666666",
                          }}
                        />
                        <FontAwesomeIcon
                          icon={['fas', 'pen']}
                          style={{
                            position: "absolute",
                            top: "50%",
                            right: "12px",
                            transform: "translateY(-50%)",
                            color: "#999999",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          position: "relative",
                        }}
                      >
                        <input
                          type="text"
                          defaultValue="なまえ なまえ"
                          placeholder="よみがなを入力"
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            padding: "8px 12px",
                            backgroundColor: "#FFFFFF",
                            width: "100%",
                            color: "#666666",
                          }}
                        />
                        <FontAwesomeIcon
                          icon={['fas', 'pen']}
                          style={{
                            position: "absolute",
                            top: "50%",
                            right: "12px",
                            transform: "translateY(-50%)",
                            color: "#999999",
                          }}
                        />
                      </div>
                      <div
                        style={{
                          position: "relative",
                        }}
                      >
                        <input
                          type="text"
                          defaultValue="test@test.test"
                          placeholder="メールアドレスを入力"
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            padding: "8px 12px",
                            backgroundColor: "#FFFFFF",
                            width: "100%",
                            color: "#666666",
                          }}
                        />
                        <FontAwesomeIcon
                          icon={['fas', 'pen']}
                          style={{
                            position: "absolute",
                            top: "50%",
                            right: "12px",
                            transform: "translateY(-50%)",
                            color: "#999999",
                          }}
                        />
                      </div>
                      <div>
                        チーム設定
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          Qaseeバージョン
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            color: "#666666",
                          }}
                        >
                          1.1.1 (OS)
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        width: "calc(100% - (420px + 48px))",
                        padding: "16px",
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          社員コード
                        </div>
                        <div
                          style={{
                            position: "relative",
                          }}
                        >
                          <input
                            type="text"
                            defaultValue="abc1234"
                            placeholder="社員コードを入力"
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              backgroundColor: "#FFFFFF",
                              width: "100%",
                              color: "#666666",
                            }}
                          />
                          <FontAwesomeIcon
                            icon={['fas', 'pen']}
                            style={{
                              position: "absolute",
                              top: "50%",
                              right: "8px",
                              transform: "translateY(-50%)",
                              color: "#999999",
                              fontSize: "10px",
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          契約形態
                        </div>
                        <div
                          style={{
                            position: "relative",
                          }}
                        >
                          <input
                            type="text"
                            defaultValue="正社員"
                            placeholder="契約形態を入力"
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              backgroundColor: "#FFFFFF",
                              width: "100%",
                              color: "#666666",
                            }}
                          />
                          <FontAwesomeIcon
                            icon={['fas', 'pen']}
                            style={{
                              position: "absolute",
                              top: "50%",
                              right: "8px",
                              transform: "translateY(-50%)",
                              color: "#999999",
                              fontSize: "10px",
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          Slack連携
                        </div>
                        <div
                          style={{
                            position: "relative",
                            padding: "5px",
                            color: "#008800",
                          }}
                        >
                          連携済み
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          Google・Microsoft連携
                        </div>
                        <div
                          style={{
                            position: "relative",
                            padding: "5px",
                            color: "#008800",
                          }}
                        >
                          Google連携済み
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          権限ロール
                        </div>
                        <div
                          style={{
                            position: "relative",
                          }}
                        >
                          <select
                            style={{
                              width: "100%",
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              color: "#666666",
                            }}
                          >
                            <option value="">
                              権限A
                            </option>
                            <option value="">
                              権限B
                            </option>
                            <option value="">
                              権限C
                            </option>
                          </select>
                          <FontAwesomeIcon
                            icon={['fas', 'caret-down']}
                            style={{
                              position: "absolute",
                              top: "50%",
                              right: "8px",
                              transform: "translateY(-50%)",
                              color: "#999999",
                              fontSize: "14px",
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          勤務地
                        </div>
                        <div
                          style={{
                            position: "relative",
                          }}
                        >
                          <input
                            type="text"
                            defaultValue="本社オフィス"
                            placeholder="勤務地を入力"
                            style={{
                              border: "solid 1px #CCCCCC",
                              borderRadius: "4px",
                              padding: "4px 8px",
                              backgroundColor: "#FFFFFF",
                              width: "100%",
                              color: "#666666",
                            }}
                          />
                          <FontAwesomeIcon
                            icon={['fas', 'pen']}
                            style={{
                              position: "absolute",
                              top: "50%",
                              right: "8px",
                              transform: "translateY(-50%)",
                              color: "#999999",
                              fontSize: "10px",
                            }}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          多要素認証
                        </div>
                        <div
                          style={{
                            position: "relative",
                            padding: "5px",
                            color: "#008800",
                          }}
                        >
                          有効
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          最終データ収集日時
                        </div>
                        <div
                          style={{
                            position: "relative",
                            padding: "5px",
                            color: "#666666",
                          }}
                        >
                          2026-06-10 12:12:12
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 4)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          休憩時間をあらかじめ登録
                        </div>
                        <div
                          style={{
                            position: "relative",
                            padding: "5px",
                            color: "#666666",
                          }}
                        >
                          12:00 ~ 13:00
                        </div>
                      </div>
                      <div
                        style={{
                          flex: "0 0 calc((100% - 24px) / 2)",
                          display: "flex",
                          flexFlow: "column",
                          gap: "2px",
                        }}
                      >
                        <div
                          style={{
                            color: "#999999",
                            fontSize: "11px",
                          }}
                        >
                          標準勤務時間
                        </div>
                        <div
                          style={{
                            position: "relative",
                            padding: "5px",
                            color: "#666666",
                          }}
                        >
                          9:00 ~ 18:00(うち休憩予定時間 1時間00分)
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      marginBottom: "-24px",
                      display: "flex",
                      gap: "16px",
                      fontWeight: "bold",
                    }}
                  >
                    {
                      TAB_LIST.map((v) => (
                        <button
                          key={`tab_${v.type}`}
                          type="button"
                          style={{
                            borderBottom: `solid 4px ${selectTab === v.type ? "#3977F2" : "transparent"}`,
                            padding: "8px 16px",
                            color: selectTab === v.type ? "#3977F2" : "#666666",
                          }}
                          onClick={() => setSelectTab(v.type)}
                        >
                          {v.label}
                        </button>
                      ))
                    }
                  </div>
                </div>
              </div>
            </section>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-detail">
                  {
                    selectTab === "display_project" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "24px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                          >
                            表示対象プロジェクト
                          </div>
                          <div>
                            <button
                              type="button"
                              style={{
                                borderRadius: "20px",
                                backgroundColor: "#81B366",
                                color: "#FFFFFF",
                              }}
                            >
                              表示対象プロジェクトを編集
                            </button>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "8px",
                          }}
                        >
                          {
                            Array(20).fill(null).map((_v, i) => (
                              <div
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  display: "flex",
                                  gap: "4px",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "8px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "12px",
                                    height: "12px",
                                    backgroundColor: "#CCCCCC",
                                  }}
                                />
                                {`プロジェクト${i}`}
                              </div>
                            ))
                          }
                        </div>
                      </>
                    )
                  }
                  {
                    selectTab === "display_category" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "24px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                          >
                            表示対象カテゴリー
                          </div>
                          <div>
                            <button
                              type="button"
                              style={{
                                borderRadius: "20px",
                                backgroundColor: "#81B366",
                                color: "#FFFFFF",
                              }}
                            >
                              表示対象カテゴリーを編集
                            </button>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "8px",
                          }}
                        >
                          {buildCategoryList()}
                        </div>
                      </>
                    )
                  }
                  {
                    selectTab === "project_and_label" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "24px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                          >
                            業務仕分け対象プロジェクト・業務
                          </div>
                          <div>
                            <button
                              type="button"
                              style={{
                                borderRadius: "20px",
                                backgroundColor: "#81B366",
                                color: "#FFFFFF",
                              }}
                            >
                              業務仕分け対象プロジェクトを編集
                            </button>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "flex-start",
                            gap: "8px",
                          }}
                        >
                          {
                            TMP_LABEL_GROUP.map((v) => (
                              <div
                                key={`suggest_label_group_${v.label_group_id}`}
                                style={{
                                  width: `calc((100% - (3 * 8px)) / 4)`,
                                  border: "solid 1px #CCCCCC",
                                  backgroundColor: "#FFFFFF",
                                }}
                              >
                                <div
                                  className="group_title"
                                  style={{
                                    display: "flex",
                                    gap: "4px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "12px",
                                    userSelect: "none",
                                    margin: 0,
                                    backgroundColor: "#FAFAFA",
                                  }}
                                  onClick={() => changeOpenLabelGroup(v.label_group_id)}
                                  role="none"
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    {
                                      v.label_group_color ? (
                                        <>
                                          <div
                                            style={{
                                              backgroundColor: v.label_group_color,
                                              width: "12px",
                                              height: "12px",
                                            }}
                                          />
                                        </>
                                      ) : (
                                        <></>
                                      )
                                    }
                                    <div
                                      style={{
                                        textOverflow: "ellipsis",
                                        overflow: "hidden",
                                        // maxWidth: `${blockWidth - 50}px`,
                                      }}
                                    >
                                      {v.label_group_name}
                                      {!!v.label_group_code && ` (${v.label_group_code})`}
                                    </div>
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "2px",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        fontSize: "8px",
                                      }}
                                    >
                                      {openLabelGroupIds.includes(v.label_group_id) ? "閉じる" : "開く"}
                                    </div>
                                    <FontAwesomeIcon
                                      icon={['fas', openLabelGroupIds.includes(v.label_group_id) ? 'angle-down' : 'angle-right']}
                                    />
                                  </div>
                                </div>
                                {
                                  openLabelGroupIds.includes(v.label_group_id) && (
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        flexFlow: "wrap",
                                        gap: "4px",
                                        border: "solid 1px #EEE",
                                        padding: "8px",
                                      }}
                                    >
                                      {
                                        v.labels.map((label) => (
                                          <div
                                            className="user_chip"
                                          >
                                            {
                                              label.label_color ? (
                                                <>
                                                  <div
                                                    style={{
                                                      backgroundColor: label.label_color,
                                                      borderRadius: "50%",
                                                      width: "12px",
                                                      height: "12px",
                                                    }}
                                                  />
                                                </>
                                              ) : (
                                                <></>
                                              )
                                            }
                                            {label.label_name}
                                            {!!label.label_code && ` (${label.label_code})`}
                                          </div>
                                        ))
                                      }
                                    </div>
                                  )
                                }
                              </div>
                            ))
                          }
                        </div>
                      </>
                    )
                  }
                  {
                    selectTab === "approval_group" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "24px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                          >
                            申請・承認グループ
                          </div>
                          <div>
                            <button
                              type="button"
                              style={{
                                borderRadius: "20px",
                                backgroundColor: "#81B366",
                                color: "#FFFFFF",
                              }}
                            >
                              申請・承認グループを編集
                            </button>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "24px",
                          }}
                        >
                          {
                            TMP_APPROVAL_GROUP.map((v) => (
                              <div
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "16px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "end",
                                  }}
                                >
                                  <button
                                    type="button"
                                    style={{
                                      border: "solid 1px #47B080",
                                      borderRadius: "4px",
                                      backgroundColor: "#E7FFE8",
                                      color: "#47B080",
                                    }}
                                  >
                                    こちらの申請・承認グループを編集
                                  </button>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "8px",
                                    marginBottom: "24px",
                                  }}
                                >
                                  {
                                    v.approvers.sort((a, b) => a.priority - b.priority).map((w, i) => (
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "8px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            width: `${(20 * i)}px`,
                                          }}
                                        />
                                        <div
                                          style={{
                                            display: "flex",
                                            flexFlow: "column",
                                            gap: "4px",
                                            position: "relative",
                                          }}
                                        >
                                          {
                                            v.approvers.length !== i + 1 && (
                                              <>
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    bottom: "-15px",
                                                    left: "7px",
                                                    width: "1px",
                                                    height: "100%",
                                                    backgroundColor: "#999999",
                                                  }}
                                                />
                                                <div
                                                  style={{
                                                    position: "absolute",
                                                    bottom: "-15px",
                                                    left: "17px",
                                                    width: "10px",
                                                    height: "1px",
                                                    backgroundColor: "#999999",
                                                    transform: "translateX(-100%)",
                                                  }}
                                                />
                                              </>
                                            )
                                          }
                                          <div
                                            style={{
                                              color: "#999999",
                                            }}
                                          >
                                            {w.team_name}
                                          </div>
                                          <div
                                            style={{
                                              display: "flex",
                                              gap: "4px",
                                            }}
                                          >
                                            <div
                                              style={{
                                                width: "14px",
                                              }}
                                            />
                                            {
                                              !!w.member_id && (
                                                <div
                                                  style={{
                                                    border: "1px solid #CCCCCC",
                                                    borderRadius: "4px",
                                                    width: "130px",
                                                    backgroundColor: "#FFFFFF",
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      backgroundColor: "#EEEEEE",
                                                      padding: "4px",
                                                      borderRadius: "4px 4px 0px 0px",
                                                      color: "#666666",
                                                      textAlign: "center",
                                                    }}
                                                  >
                                                    {!i ? "最終" : `${v.approvers.length - i}次`}
                                                    承認者
                                                  </div>
                                                  <div
                                                    style={{
                                                      padding: "4px",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      gap: "4px",
                                                      backgroundColor: "#FFFFFF",
                                                    }}
                                                  >
                                                    <img
                                                      alt="profile"
                                                      // eslint-disable-next-line max-len
                                                      src={w.member_profile_image ? w.member_profile_image : `/static/images/profile/${(Number(w.member_id) || 0) % 12}.jpeg`}
                                                      className="profile_icon"
                                                      style={{
                                                        width: "20px",
                                                        height: "20px",
                                                        borderRadius: "50%",
                                                      }}
                                                    />
                                                    <ruby>
                                                      {w.member_name}
                                                      <rt>{w.member_kana}</rt>
                                                    </ruby>
                                                  </div>
                                                </div>
                                              )
                                            }
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  }
                                </div>
                                <div>
                                  <div
                                    style={{
                                      padding: "8px 4px",
                                    }}
                                  >
                                    申請者
                                  </div>
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: "4px",
                                      flexWrap: "wrap",
                                      borderRadius: "4px",
                                      padding: "8px",
                                      backgroundColor: "#F0F1F5",
                                    }}
                                  >
                                    {
                                      v.members.map((w) => (
                                        <div
                                          style={{
                                            padding: "4px 8px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            gap: "4px",
                                            border: "solid 1px #CCCCCC",
                                            borderRadius: "20px",
                                            backgroundColor: "#FFFFFF",
                                            fontSize: "10px",
                                          }}
                                        >
                                          <img
                                            alt="profile"
                                            // eslint-disable-next-line max-len
                                            src={w.member_profile_image ? w.member_profile_image : `/static/images/profile/${(Number(w.member_id) || 0) % 12}.jpeg`}
                                            className="profile_icon"
                                            style={{
                                              width: "20px",
                                              height: "20px",
                                              borderRadius: "50%",
                                            }}
                                          />
                                          <ruby>
                                            {w.member_name}
                                            <rt>{w.member_kana}</rt>
                                          </ruby>
                                        </div>
                                      ))
                                    }
                                  </div>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </>
                    )
                  }
                  {
                    selectTab === "mail_setting" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "24px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "16px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                }}
                              >
                                配信メール
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "16px",
                              }}
                            >
                              <div
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "8px 16px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "16px",
                                    color: "#008800",
                                  }}
                                >
                                  <FontAwesomeIcon icon={['fas', 'check-square']} />
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "14px",
                                    }}
                                  >
                                    QaseeClient未起動メールを受信する
                                  </div>
                                  <div
                                    style={{
                                      color: "#666666",
                                    }}
                                  >
                                    QaseeClientが未起動のユーザーがいた時、あなたに未起動ユーザーをお知らせするメールを送信しています
                                  </div>
                                </div>
                              </div>
                              <div
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "8px 16px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: "16px",
                                    color: "#008800",
                                  }}
                                >
                                  <FontAwesomeIcon icon={['fas', 'check-square']} />
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "4px",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "14px",
                                    }}
                                  >
                                    申請・承認お知らせメールを受信する
                                  </div>
                                  <div
                                    style={{
                                      color: "#666666",
                                    }}
                                  >
                                    あなたに関係する申請および承認時、あなたにお知らせメールを送信しています
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: "16px",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "bold",
                                }}
                              >
                                アラート
                              </div>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                flexFlow: "column",
                                gap: "16px",
                              }}
                            >
                              {
                                TMP_ALERT.map((v) => (
                                  <div
                                    style={{
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "4px",
                                      padding: "8px 16px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "space-between",
                                      gap: "16px",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: "16px",
                                          color: "#008800",
                                        }}
                                      >
                                        <FontAwesomeIcon icon={['fas', 'check-square']} />
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          gap: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "14px",
                                          }}
                                        >
                                          {`アラート名：${v.name}`}
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "16px",
                                      }}
                                    >
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "baseline",
                                          gap: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "10px",
                                          }}
                                        >
                                          アラート対象
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {v.target}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "baseline",
                                          gap: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "10px",
                                          }}
                                        >
                                          アラート受信人数
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {`${v.count}人`}
                                        </div>
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "baseline",
                                          gap: "4px",
                                        }}
                                      >
                                        <div
                                          style={{
                                            fontSize: "10px",
                                          }}
                                        >
                                          アラート頻度
                                        </div>
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {v.cycle}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                        </div>
                      </>
                    )
                  }
                  {
                    selectTab === "other_setting" && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "24px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                          >
                            その他の設定
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexFlow: "column",
                            gap: "16px",
                          }}
                        >
                          {
                            OTHER_SETTING_LIST.map((v) => (
                              <div
                                style={{
                                  backgroundColor: "#f9fdff",
                                  border: "solid 1px #CCCCCC",
                                  borderRadius: "4px",
                                  padding: "8px 16px",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    gap: "4px",
                                    width: "550px",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "14px",
                                    }}
                                  >
                                    {v.title}
                                  </div>
                                  <div
                                    style={{
                                      color: "#666666",
                                    }}
                                  >
                                    {v.description}
                                  </div>
                                </div>
                                <div>
                                  <select
                                    style={{
                                      border: "solid 1px #999999",
                                      padding: "8px 4px",
                                      borderRadius: "4px",
                                      appearance: "auto",
                                      cursor: "pointer",
                                      backgroundColor: "#ffffff",
                                    }}
                                  >
                                    <option value="">
                                      会社設定に準拠する(会社設定：ON)
                                    </option>
                                    <option value="">
                                      ON
                                    </option>
                                    <option value="">
                                      OFF
                                    </option>
                                  </select>
                                </div>
                              </div>
                            ))
                          }
                        </div>
                      </>
                    )
                  }
                </div>
              </div>
            </section>
          </>
        )
      }
    </section>
  )
}

export default Detail
